import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CollectionErrorState from "@/components/collections/CollectionErrorState";
import ProductFilterBar from "@/components/collections/ProductFilterBar";
import ProductCard from "@/components/products/ProductCard";
import ProductDetailSlideIn from "@/components/products/ProductDetailSlideIn";
import ProductEmptyState from "@/components/products/ProductEmptyState";
import SendGiftModal from "@/components/products/SendGiftModal";
import { useCollectionProducts } from "@/hooks/useCollectionProducts";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useProductDetails } from "@/hooks/useProductDetails";
import { useProductFilters } from "@/hooks/useProductFilters";
import type { Product } from "@/interfaces/product";

// ─── Loading skeleton ──────────────────────────────────────────────────────────

const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f", "g", "h"];

function ProductGridSkeleton({ isMobile }: { isMobile: boolean }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
        gap: 3,
      }}
    >
      {SKELETON_KEYS.map((key) => (
        <Box key={key} sx={{ borderRadius: "16px", overflow: "hidden" }}>
          <Skeleton variant="rectangular" height={264} sx={{ bgcolor: "#f0f0f0" }} />
          <Box sx={{ px: "12px", py: "16px", display: "flex", flexDirection: "column", gap: 1 }}>
            <Skeleton variant="rounded" width={90} height={20} sx={{ bgcolor: "#f0f0f0" }} />
            <Skeleton variant="text" width="70%" sx={{ bgcolor: "#f0f0f0" }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function SingleCollection() {
  const { id } = useParams<{ id: string }>();

  // Slide-in state
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [slideInOpen, setSlideInOpen] = useState(false);

  const {
    data: detailedProduct = null,
    isLoading: isProductLoading,
    isError: isProductError,
  } = useProductDetails(selectedProductId);

  // Send modal state
  const [sendModalOpen, setSendModalOpen] = useState(false);

  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const { data: rawProducts = [], isLoading, isError } = useCollectionProducts(id);

  const products: Product[] = useMemo(() => rawProducts.map((p) => p), [rawProducts]);

  // "Ships to" country — filters physical gifts by shippability (charity/experience unaffected).
  const [selectedCountry, setSelectedCountry] = useState("US");

  // Display-type / Category / Cause / country filtering (shared with the tier detail view).
  const { filtered, barProps } = useProductFilters(products, selectedCountry);

  const handleViewMore = (product: Product) => {
    setSelectedProductId(product.id);
    setSlideInOpen(true);
  };

  const handleSlideInClose = () => {
    setSlideInOpen(false);
  };

  const handleSendGift = () => {
    setSendModalOpen(true);
  };

  const handleSendGiftOnly = () => {
    setSendModalOpen(false);
    setSlideInOpen(false);
    navigate("/campaigns");
  };

  const handleSendCollection = () => {
    setSendModalOpen(false);
    setSlideInOpen(false);
    navigate("/campaigns");
  };

  const {
    renderedItems: paginatedProducts,
    hasMore,
    sentinelRef,
  } = useInfiniteScroll(filtered, 24);

  // ── Main render ─────────────────────────────────────────────────
  return (
    <>
      <ProductFilterBar
        {...barProps}
        selectedCountry={selectedCountry}
        onCountryChange={setSelectedCountry}
      />

      {/* ── Product detail slide-in ───────────────────────────────── */}
      <ProductDetailSlideIn
        open={slideInOpen}
        product={detailedProduct}
        loading={isProductLoading}
        error={isProductError}
        onClose={handleSlideInClose}
        onSendGift={handleSendGift}
      />

      {/* ── Product grid ─────────────────────────────────────────── */}
      {isLoading ? (
        <ProductGridSkeleton isMobile={isMobile} />
      ) : isError ? (
        <CollectionErrorState />
      ) : filtered.length === 0 ? (
        <ProductEmptyState />
      ) : (
        <>
          <Box
            data-testid="product-grid"
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
              gap: 3,
            }}
          >
            {paginatedProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => handleViewMore(product)}
              />
            ))}
          </Box>
          {hasMore && <div ref={sentinelRef} style={{ height: "1px" }} aria-hidden="true" />}
        </>
      )}
      {/* ── Send gift modal ───────────────────────────────────────── */}
      <SendGiftModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        onSendGiftOnly={handleSendGiftOnly}
        onSendCollection={handleSendCollection}
      />
    </>
  );
}
